import React, { useState, useRef } from 'react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { X, Camera, Check, Trash2 } from 'lucide-react';
import { compressImage } from '../../core/imageUtils';

const AddNotesModal = ({ isOpen, onClose, initialNotes, initialPhotos = [], onSave }) => {
  const { t } = useLang();
  const [notes, setNotes] = useState(initialNotes || "");
  const [photos, setPhotos] = useState(initialPhotos || []);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleAddPhotoClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const compressedPhotos = [];
      for (const file of files) {
        try {
          const compressed = await compressImage(file);
          compressedPhotos.push({
            file: compressed,
            previewUrl: URL.createObjectURL(compressed)
          });
        } catch (err) {
          console.error("Compression error:", err);
          compressedPhotos.push({
            file,
            previewUrl: URL.createObjectURL(file)
          });
        }
      }
      setPhotos(prev => [...prev, ...compressedPhotos]);
    }
  };

  const handleRemovePhoto = (index) => {
    const photoToRemove = photos[index];
    if (photoToRemove && photoToRemove.previewUrl) {
      URL.revokeObjectURL(photoToRemove.previewUrl);
    }
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
           <h3 style={styles.title}>{t(LangKeys.add_bill_add_notes_photos)}</h3>
           <button style={styles.closeBtn} onClick={onClose}><X size={20} /></button>
        </div>
        
        <div style={styles.content}>
           <div style={styles.section}>
              <label style={styles.label}>{t(LangKeys.notes || 'Private Notes')}</label>
              <textarea 
                style={styles.textarea} 
                value={notes} 
                onChange={(e) => setNotes(e.target.value)} 
                placeholder="Write something for your reference..."
              />
           </div>

           <div style={styles.section}>
              <label style={styles.label}>{t(LangKeys.photos || 'Photos')}</label>
              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleFileChange} 
                multiple 
                accept="image/*" 
                style={{ display: 'none' }} 
              />
              <div style={styles.photoGrid}>
                 <div style={styles.addPhotoBtn} onClick={handleAddPhotoClick}>
                    <Camera size={24} color="#94A3B8" />
                    <span style={{fontSize: '11px', fontWeight: '800', color: '#94A3B8'}}>ADD PHOTO</span>
                 </div>
                 {photos.map((photo, i) => {
                   const src = typeof photo === 'string' ? photo : photo.previewUrl;
                   return (
                     <div key={i} style={styles.photoThumb}>
                        <img 
                          src={src} 
                          alt="Attachment preview" 
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' }} 
                        />
                        <button style={styles.delPhoto} onClick={() => handleRemovePhoto(i)}>
                          <Trash2 size={12} color="white" />
                        </button>
                     </div>
                   );
                 })}
              </div>
           </div>
           
           <p style={styles.hint}>{t(LangKeys.add_bill_private_to_you)}</p>
        </div>

        <div style={styles.footer}>
           <button style={styles.saveBtn} onClick={() => onSave({ notes, photos })}>
              <Check size={18} /> <span>DONE</span>
           </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 },
  modal: { width: '100%', maxWidth: '500px', background: 'white', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' },
  header: { padding: '20px 24px', borderBottom: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: 0 },
  closeBtn: { border: 'none', background: 'transparent', cursor: 'pointer', color: '#94A3B8' },
  content: { padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' },
  section: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { fontSize: '12px', fontWeight: '800', color: '#94A3B8', textTransform: 'uppercase' },
  textarea: { width: '100%', height: '120px', padding: '16px', borderRadius: '12px', border: '1px solid #E2E8F0', background: '#F8FAFC', fontSize: '14px', fontWeight: '600', outline: 'none', resize: 'none', fontFamily: 'inherit' },
  photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' },
  addPhotoBtn: { height: '80px', borderRadius: '12px', border: '2px dashed #E2E8F0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  photoThumb: { height: '80px', borderRadius: '12px', background: '#F1F5F9', position: 'relative' },
  delPhoto: { position: 'absolute', top: '4px', right: '4px', width: '20px', height: '20px', borderRadius: '10px', background: 'rgba(239, 68, 68, 0.8)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  hint: { fontSize: '11px', color: '#94A3B8', fontWeight: '700', textTransform: 'uppercase', margin: 0 },
  footer: { padding: '16px 24px', background: '#F8FAFC', borderTop: '1px solid #F1F5F9' },
  saveBtn: { width: '100%', height: '48px', borderRadius: '12px', border: 'none', background: '#1D4ED8', color: 'white', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }
};

export default AddNotesModal;
